// src/views/AdminView.js — extracted from App.js
import React, { useState, useRef, useEffect, useCallback } from "react";
import { supabase, upsertOne, deleteFromDB } from "../lib/supabase";
import { sendEmailNotif, thcEmailHtml, capabilitiesHtml, timeAgo, groupCatalogEntries } from "../lib/utils";
import { SK, ADMIN_EMAIL, STATUSES } from "../constants";
import { LANG_OPTIONS } from "../lib/i18n";
import { BetaApprovePanel } from "../components/BetaApprovePanel";
import { MaintenanceToggle, AiDashboard } from "../components/MaintenanceToggle";
import { AdminCatalogEdit } from "../components/AdminCatalogEdit";
import { RatingModal } from "../components/RatingModal";

// ─── Credit Adjust widget (inside Users tab) ──────────────────────────────────
function CreditAdjust({ u, addCredit, toast, saveUsers, users }) {
  const [open, setOpen]   = useState(false);
  const [amt, setAmt]     = useState("");
  const [saving, setSaving] = useState(false);
  const credits = u.credits || 0;

  const apply = async (sign) => {
    const n = parseInt(amt, 10);
    if (!n || n <= 0) { toast("Enter a valid amount."); return; }
    setSaving(true);
    await addCredit(u.id, sign * n, null, `Manual adjustment by admin`);
    setAmt(""); setOpen(false); setSaving(false);
    toast(`₹${n} ${sign > 0 ? "added to" : "deducted from"} ${u.name}'s credit.`);
  };

  return (
    <div style={{marginBottom:10}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:open?8:0}}>
        <span style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:credits>0?"var(--grn)":"var(--ink3)"}}>
          ₹{credits} credit
        </span>
        <button onClick={()=>setOpen(p=>!p)} style={{fontSize:9,padding:"2px 8px",borderRadius:6,border:"1px solid var(--bd)",background:"transparent",cursor:"pointer",fontFamily:"'DM Mono',monospace",color:"var(--ink3)"}}>
          {open?"✕":"adjust"}
        </button>
      </div>
      {open && (
        <div style={{display:"flex",gap:6,alignItems:"center"}}>
          <input type="number" min="1" value={amt} onChange={e=>setAmt(e.target.value)}
            placeholder="₹ amount"
            style={{border:"1.5px solid var(--bd)",borderRadius:6,padding:"5px 8px",fontSize:12,width:100,fontFamily:"'DM Sans',sans-serif"}}/>
          <button disabled={saving} onClick={()=>apply(1)}
            style={{fontSize:11,padding:"5px 10px",borderRadius:6,border:"1.5px solid var(--grn)",background:"#F0FDF4",color:"var(--grn)",cursor:"pointer",fontFamily:"'DM Mono',monospace"}}>
            + Add
          </button>
          <button disabled={saving} onClick={()=>apply(-1)}
            style={{fontSize:11,padding:"5px 10px",borderRadius:6,border:"1.5px solid var(--rbd)",background:"#FFF1F2",color:"var(--red)",cursor:"pointer",fontFamily:"'DM Mono',monospace"}}>
            − Deduct
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Admin Stories Tab ────────────────────────────────────────────────────────
function extractYTId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

function AdminStoriesTab({ toast }) {
  const emptyForm = { youtube_url:"", title:"", caption:"", submitted_by:"", featured:false };
  const [form, setForm]       = useState(emptyForm);
  const [stories, setStories] = useState([]);
  const [saving, setSaving]   = useState(false);

  const loadStories = useCallback(async () => {
    const { data } = await supabase.from("hmt_stories").select("*").order("created_at", { ascending: false });
    setStories(data || []);
  }, []);

  useEffect(() => { loadStories(); }, [loadStories]);

  const handleAdd = async () => {
    const ytId = extractYTId(form.youtube_url);
    if (!ytId) { toast("Could not extract YouTube video ID from that URL."); return; }
    if (!form.title.trim()) { toast("Title is required."); return; }
    setSaving(true);
    const row = {
      id: `story-${Date.now()}`,
      youtube_url: form.youtube_url.trim(),
      youtube_id: ytId,
      title: form.title.trim(),
      caption: form.caption.trim() || null,
      submitted_by: form.submitted_by.trim() || null,
      featured: form.featured,
      created_at: new Date().toISOString(),
    };
    await supabase.from("hmt_stories").insert(row);
    toast("Story added.");
    setForm(emptyForm);
    loadStories();
    setSaving(false);
  };

  const handleDelete = async (id) => {
    await supabase.from("hmt_stories").delete().eq("id", id);
    await supabase.from("story_likes").delete().eq("story_id", id);
    setStories(prev => prev.filter(s => s.id !== id));
    toast("Story removed.");
  };

  const toggleFeatured = async (story) => {
    const val = !story.featured;
    await supabase.from("hmt_stories").update({ featured: val }).eq("id", story.id);
    setStories(prev => prev.map(s => s.id === story.id ? { ...s, featured: val } : s));
  };

  const inp = { border:"1.5px solid var(--bd)", borderRadius:8, padding:"9px 12px", fontSize:13, width:"100%", fontFamily:"'DM Sans',sans-serif", background:"#fff", boxSizing:"border-box" };

  return (
    <div>
      {/* Add form */}
      <div className="card" style={{marginBottom:24}}>
        <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:"0.12em",color:"var(--accent)",marginBottom:14}}>ADD STORY</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
          <div style={{gridColumn:"1/-1"}}>
            <div style={{fontSize:11,color:"var(--ink3)",marginBottom:4}}>YouTube URL *</div>
            <input style={inp} placeholder="https://youtube.com/watch?v=..." value={form.youtube_url} onChange={e=>setForm(p=>({...p,youtube_url:e.target.value}))}/>
          </div>
          <div style={{gridColumn:"1/-1"}}>
            <div style={{fontSize:11,color:"var(--ink3)",marginBottom:4}}>Title *</div>
            <input style={inp} placeholder="My HMT Janata — 40 years on my father's wrist" value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))}/>
          </div>
          <div>
            <div style={{fontSize:11,color:"var(--ink3)",marginBottom:4}}>Caption (optional)</div>
            <input style={inp} placeholder="Short description shown below title" value={form.caption} onChange={e=>setForm(p=>({...p,caption:e.target.value}))}/>
          </div>
          <div>
            <div style={{fontSize:11,color:"var(--ink3)",marginBottom:4}}>Submitted by (optional)</div>
            <input style={inp} placeholder="Contributor name / handle" value={form.submitted_by} onChange={e=>setForm(p=>({...p,submitted_by:e.target.value}))}/>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
          <label style={{display:"flex",alignItems:"center",gap:6,fontSize:13,cursor:"pointer"}}>
            <input type="checkbox" checked={form.featured} onChange={e=>setForm(p=>({...p,featured:e.target.checked}))} style={{width:15,height:15}}/>
            Featured (pinned to top)
          </label>
        </div>
        {form.youtube_url && extractYTId(form.youtube_url) && (
          <img src={`https://img.youtube.com/vi/${extractYTId(form.youtube_url)}/hqdefault.jpg`} alt="preview" style={{width:180,borderRadius:8,marginBottom:12,border:"1.5px solid var(--bd)"}}/>
        )}
        <button onClick={handleAdd} disabled={saving} style={{background:"#111118",color:"#fff",border:"none",borderRadius:8,padding:"10px 20px",fontSize:13,cursor:saving?"not-allowed":"pointer",fontFamily:"'DM Sans',sans-serif"}}>
          {saving ? "Adding…" : "Add Story"}
        </button>
      </div>

      {/* Existing stories */}
      {stories.length === 0 ? (
        <div style={{textAlign:"center",padding:48,fontFamily:"'DM Mono',monospace",fontSize:12,color:"var(--ink3)"}}>No stories yet.</div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {stories.map(s => (
            <div key={s.id} className="card" style={{display:"flex",gap:14,alignItems:"flex-start"}}>
              <img src={`https://img.youtube.com/vi/${s.youtube_id}/hqdefault.jpg`} alt={s.title} style={{width:100,borderRadius:6,flexShrink:0,objectFit:"cover",aspectRatio:"16/9"}}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:15,fontStyle:"italic",marginBottom:3}}>{s.title}</div>
                {s.caption && <div style={{fontSize:12,color:"var(--ink3)",marginBottom:3}}>{s.caption}</div>}
                {s.submitted_by && <div style={{fontSize:11,color:"var(--ink2)"}}>by {s.submitted_by}</div>}
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",marginTop:4}}>{s.id}</div>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:6,flexShrink:0}}>
                <button onClick={()=>toggleFeatured(s)} style={{fontSize:11,padding:"4px 10px",borderRadius:6,border:`1.5px solid ${s.featured?"#D97706":"var(--bd)"}`,background:s.featured?"#FEF3C7":"transparent",cursor:"pointer",color:s.featured?"#D97706":"var(--ink2)",fontFamily:"'DM Mono',monospace"}}>
                  {s.featured ? "★ Featured" : "☆ Feature"}
                </button>
                <button onClick={()=>handleDelete(s.id)} style={{fontSize:11,padding:"4px 10px",borderRadius:6,border:"1.5px solid var(--red)",background:"transparent",cursor:"pointer",color:"var(--red)",fontFamily:"'DM Mono',monospace"}}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── ADMIN VIEW ───────────────────────────────────────────────────────────────
export function AdminView({ inventory, saveInventory, requests, saveRequests, sellers, saveSellers, listings, saveListings, users, saveUsers, collectorRequests, saveCRs, catalogEntries, saveCatalog, betaRequests, saveBetaReqs, watchModels=[], saveWMs, watchDesigns=[], saveDesigns, designOrders=[], saveDesignOrders, aiEnabled, toggleAi, maintenance, toggleMaintenance, tickerEnabled, toggleTicker, aiBannerEnabled, toggleAiBanner, navStates={}, setNavState, session, toast, addNotif, reviews=[], saveReviews, initialTab="requests", onTabChange, addCredit, isModerator=false }) {
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [tab,setTab]       = useState(isModerator && !["sellers","catalog_rev","collector_req"].includes(initialTab) ? "sellers" : initialTab);
  const switchTab = (t) => { setTab(t); onTabChange?.(t); };
  const [nw,setNw]         = useState({brand:"",model:"",reference:"",decade:"",condition:"",price:"",description:"",tags:"",available:true});
  const [sel,setSel]       = useState({});
  const [note,setNote]     = useState({});
  const [crNote,setCRNote] = useState({});
  const [resubmitOpen, setResubmitOpen] = useState({}); // sellerId → note text being typed
  const [viewingId,setViewingId] = useState(null);
  const [invLink, setInvLink] = useState({name:"",city:"",phone:"",generating:false});
  const uNw=(k,v)=>setNw(p=>({...p,[k]:v}));
  // STATUSES imported from constants

  const deleteRequest = async (r) => {
    const hasPhotos = r.photos && r.photos.length > 0;
    const msg = hasPhotos
      ? `Delete request ${r.id} (${r.watch})? It has ${r.photos.length} photo(s). This cannot be undone.`
      : `Delete request ${r.id} (${r.watch})? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try { await deleteFromDB(SK.requests, r.id); } catch { toast("Delete failed — please retry."); return; }
    saveRequests(requests.filter(req => req.id !== r.id));
    toast("Request deleted.");
  };

  const updateStatus = async (rid) => {
    if(!sel[rid])return;
    const r = requests.find(req=>req.id===rid);
    if (!r) return;
    const newStatus = sel[rid];
    const escrowPatch =
      newStatus==="Completed" || newStatus==="Dispute Resolved — Seller" ? {escrowStatus:"released"}
      : newStatus==="Dispute Resolved — Buyer" ? {escrowStatus:"refunded"}
      : {};
    const creditAmount = newStatus==="Cannot Source" ? (r.amount||99) : 0;
    const creditNote = creditAmount > 0 ? ` ₹${creditAmount} added to your account credit.` : "";
    const updated = {...r, status:newStatus, ...escrowPatch, updates:[...(r.updates||[]),{date:new Date().toISOString(),note:note[rid]||`Status: ${newStatus}${creditNote}`}]};
    try { await upsertOne(SK.requests, updated); } catch { toast("Save failed — please retry."); return; }
    saveRequests(requests.map(req=>req.id!==rid?req:updated));
    if (creditAmount > 0 && addCredit) {
      await addCredit(r.userId, creditAmount, rid, `Refund for unsourced request: ${r.watch}`);
    }
    setSel(p=>({...p,[rid]:""})); setNote(p=>({...p,[rid]:""})); toast("Updated.");
    if (addNotif) addNotif(r.userId, {type:"status", title:`Request update — ${r.watch}`, body:`Your request is now: ${newStatus}${creditNote}${note[rid]?" — "+note[rid]:""}`, link:{view:"request"}, emailTo:r.email, emailSubject:`Update on your ${r.watch} request`, whatsappTo:r.phone, whatsappTemplate:"thc_buyer_update", whatsappParams:[{name:"1",value:r.name||"Collector"},{name:"2",value:r.watch},{name:"3",value:`Status: ${newStatus}${creditNote}${note[rid]?" — "+note[rid]:""}`}]});
  };

  const approveCollector = async (cr) => {
    const crPatch = {...collectorRequests.find(r=>r.id===cr.id), status:"approved", adminNote:crNote[cr.id]||""};
    const existingUser = users.find(u=>u.id===cr.userId);
    const roles = existingUser ? [...new Set([...(existingUser.roles||[existingUser.role].filter(Boolean)),"collector"])] : ["collector"];
    const userPatch = {id:cr.userId, role:"collector", roles};
    try {
      await upsertOne(SK.collectorRequests, crPatch);
      await upsertOne(SK.users, userPatch);
    } catch { toast("Approval failed — please retry."); return; }
    saveCRs(collectorRequests.map(r=>r.id!==cr.id?r:crPatch));
    saveUsers(users.map(u=>u.id!==cr.userId?u:{...u,...userPatch}));
    toast(`${cr.name} is now a Collector.`);
    if (addNotif) addNotif(cr.userId, {type:"approval", title:"You're now a Collector ✦", body:"Your collector application has been approved. You can now add watches to the catalog.", link:{view:"add-catalog"}, emailTo:cr.email, emailSubject:"Welcome to THC.watch — you're now a Collector"});
  };
  const rejectCollector = async (cr) => {
    const crPatch = {...collectorRequests.find(r=>r.id===cr.id), status:"rejected", adminNote:crNote[cr.id]||""};
    try { await upsertOne(SK.collectorRequests, crPatch); } catch { toast("Rejection failed — please retry."); return; }
    saveCRs(collectorRequests.map(r=>r.id!==cr.id?r:crPatch));
    toast("Request rejected.");
  };
  const approveCatalog = async (entry) => {
    const wmKey = `${(entry.brand||"").toLowerCase().trim()}|||${(entry.model||"").toLowerCase().trim()}`;
    let wm = watchModels.find(w => `${(w.brand||"").toLowerCase().trim()}|||${(w.model||"").toLowerCase().trim()}` === wmKey);
    let watchModelId = wm?.id;
    try {
      if (!watchModelId && entry.brand && entry.model) {
        watchModelId = "wm-" + Date.now();
        const newWm = { id:watchModelId, brand:entry.brand, model:entry.model, reference:entry.reference||"", decade:entry.decade||"", movement:entry.movement||"", company:entry.company||"", description:entry.description||"", photos:entry.photos||[], createdAt:new Date().toISOString() };
        await upsertOne(SK.watchModels, newWm);
        saveWMs([...watchModels, newWm]);
      }
      await upsertOne(SK.catalogEntries, {...entry, status:"approved", watchModelId});
    } catch { toast("Approval failed — please retry."); return; }
    saveCatalog(catalogEntries.map(e=>e.id!==entry.id?e:{...e,status:"approved",watchModelId}));
    toast("Entry approved — now live in catalog.");
    if (addNotif && entry.collectorId) {
      const cu = users.find(u=>u.id===entry.collectorId);
      addNotif(entry.collectorId, {type:"approval", title:`Catalog entry approved — ${entry.brand} ${entry.model}`, body:"Your submission is now live in the catalog.", link:{view:"catalog"}, emailTo:cu?.email, emailSubject:`Your ${entry.brand} ${entry.model} is now live in the catalog`});
    }
  };
  const rejectCatalog = async (entry) => {
    try {
      const { error } = await supabase.from(SK.catalogEntries).delete().eq("id", entry.id);
      if (error) throw error;
    } catch { toast("Delete failed — please retry."); return; }
    saveCatalog(catalogEntries.filter(e=>e.id!==entry.id));
    toast("Entry rejected and removed.");
  };
  const hideCatalog = async (entry) => {
    try { await upsertOne(SK.catalogEntries, {...entry, status:"hidden"}); } catch { toast("Save failed — please retry."); return; }
    saveCatalog(catalogEntries.map(e=>e.id!==entry.id?e:{...e,status:"hidden"}));
    toast("Entry hidden from catalog.");
  };
  const unhideCatalog = async (entry) => {
    try { await upsertOne(SK.catalogEntries, {...entry, status:"approved"}); } catch { toast("Save failed — please retry."); return; }
    saveCatalog(catalogEntries.map(e=>e.id!==entry.id?e:{...e,status:"approved"}));
    toast("Entry restored to catalog.");
  };
  const deleteCatalogEntry = async (entry) => {
    if (!window.confirm(`Delete "${entry.brand} ${entry.model}"? This cannot be undone.`)) return;
    try {
      const { error } = await supabase.from(SK.catalogEntries).delete().eq("id", entry.id);
      if (error) throw error;
    } catch { toast("Delete failed — please retry."); return; }
    saveCatalog(catalogEntries.filter(e=>e.id!==entry.id));
    toast("Entry permanently deleted.");
  };

  const pendingCRs = collectorRequests.filter(r=>r.status==="pending");
  const pendingCatalog = catalogEntries.filter(e=>e.status==="pending");

  const mergeRank = e => !e.entryType?0:e.entryType==="for_sale"?1:2;

  // Groups of 2+ catalog entries OR watch_models sharing the same brand+model
  const mergeCandidates = (() => {
    const map = new Map();
    for (const e of catalogEntries||[]) {
      const key = `${(e.brand||"").toLowerCase().trim()}|||${(e.model||"").toLowerCase().trim()}`;
      if (!map.has(key)) map.set(key, { entries:[], wms:[] });
      map.get(key).entries.push(e);
    }
    for (const wm of watchModels||[]) {
      const key = `${(wm.brand||"").toLowerCase().trim()}|||${(wm.model||"").toLowerCase().trim()}`;
      if (map.has(key)) map.get(key).wms.push(wm);
    }
    return [...map.values()]
      .map(g => ({ ...g, entries: [...g.entries].sort((a,b)=>mergeRank(a)-mergeRank(b)) }))
      .filter(g => g.entries.length > 1 || g.wms.length > 1);
  })();

  const handleMerge = async (group) => {
    const { entries, wms } = group;
    const master = entries[0];
    const rest   = entries.slice(1);
    const restIds = new Set(rest.map(e=>e.id));
    // Deduplicated photo pool
    const allPhotos = [...new Set([
      ...(master.photos||[]),
      ...rest.flatMap(e=>e.photos||[]),
    ])];
    // Persist master update with pooled photos
    try { await upsertOne(SK.catalogEntries, {...master, photos:allPhotos}); }
    catch { toast("DB write failed — please retry."); return; }
    // Delete merged catalog entries
    for (const e of rest) {
      try { await supabase.from(SK.catalogEntries).delete().eq("id", e.id); } catch {}
    }
    // Re-link requests pointing to deleted entries
    const affected = (requests||[]).filter(r=>restIds.has(r.catalogEntryId));
    for (const r of affected) {
      try { await upsertOne(SK.requests, {...r, catalogEntryId:master.id}); } catch {}
    }
    // Merge duplicate watch_models if present
    if (wms.length > 1) {
      const masterWm = wms[0];
      const restWms  = wms.slice(1);
      const restWmIds = new Set(restWms.map(w=>w.id));
      const wmPhotos = [...new Set([...(masterWm.photos||[]), ...restWms.flatMap(w=>w.photos||[])])];
      try { await upsertOne(SK.watchModels, {...masterWm, photos:wmPhotos}); } catch {}
      // Re-link catalog entries that pointed to deleted watch_models
      const wmAffected = (catalogEntries||[]).filter(e=>restWmIds.has(e.watchModelId));
      for (const e of wmAffected) {
        try { await upsertOne(SK.catalogEntries, {...e, watchModelId:masterWm.id}); } catch {}
      }
      for (const w of restWms) {
        try { await supabase.from(SK.watchModels).delete().eq("id", w.id); } catch {}
      }
      if (saveWMs) saveWMs((watchModels||[]).map(w=>{
        if (w.id===masterWm.id) return {...w, photos:wmPhotos};
        if (restWmIds.has(w.id)) return null;
        return w;
      }).filter(Boolean));
    }
    // Update local state
    saveCatalog((catalogEntries||[]).map(e=>{
      if (e.id===master.id) return {...e, photos:allPhotos};
      if (restIds.has(e.id)) return null;
      return e;
    }).filter(Boolean));
    saveRequests((requests||[]).map(r=>restIds.has(r.catalogEntryId)?{...r,catalogEntryId:master.id}:r));
    toast(`Merged ${entries.length} entries — photos pooled under ${master.brand} ${master.model}.`);
  };

  const pendingBeta = (betaRequests||[]).filter(r=>r.status==="pending");
  const pendingSellers = sellers.filter(s=>!s.verified);
  const disputedCount = requests.filter(r=>r.status==="Disputed").length;
  const pendingDesigns = (watchDesigns||[]).filter(d=>d.status==="pending");
  const designOrderCount = (did) => (designOrders||[]).filter(o=>o.designId===did).reduce((a,o)=>a+(o.quantity||1),0);
  const approveDesign = (d) => {
    saveDesigns((watchDesigns||[]).map(x=>x.id!==d.id?x:{...x,status:"approved",adminNote:null}));
    toast("Design approved — now live in Watch Labs.");
    if (addNotif) {
      const cu = users.find(u=>u.id===d.designerId);
      addNotif(d.designerId, {type:"approval", title:`Design approved — ${d.name}`, body:"Your design is live in Watch Labs. Collectors can now pre-order.", link:{view:"labs"}, emailTo:cu?.email, emailSubject:`Your design "${d.name}" is now live in Watch Labs`});
    }
  };
  const rejectDesign = (d, reason) => {
    saveDesigns((watchDesigns||[]).map(x=>x.id!==d.id?x:{...x,status:"rejected",adminNote:reason||null}));
    toast("Design rejected.");
    if (addNotif) {
      const cu = users.find(u=>u.id===d.designerId);
      addNotif(d.designerId, {type:"system", title:`Design declined — ${d.name}`, body:reason||"Your design wasn't approved. See Watch Labs → My Designs for details.", link:{view:"labs"}, emailTo:cu?.email, emailSubject:`Update on your Watch Labs submission`});
    }
  };
  const advanceDesign = (d, newStatus) => {
    saveDesigns((watchDesigns||[]).map(x=>x.id!==d.id?x:{...x,status:newStatus}));
    toast(`Status set to ${newStatus.replace(/_/g," ")}.`);
    if (addNotif) {
      const label = newStatus==="in_production" ? "in production" : newStatus==="produced" ? "produced" : newStatus;
      const cu = users?.find(u=>u.id===d.designerId);
      addNotif(d.designerId, {type:"status", title:`"${d.name}" — ${label}`, body:`Your Watch Labs design is now ${label}.`, link:{view:"labs"}, emailTo:cu?.email, emailSubject:`Watch Labs update — "${d.name}" is now ${label}`});
    }
  };
  const deleteDesign = (d) => {
    if (!window.confirm(`Delete design "${d.name}"? This cannot be undone.`)) return;
    saveDesigns((watchDesigns||[]).filter(x=>x.id!==d.id));
    deleteFromDB(SK.watchDesigns, d.id);
    toast("Design permanently deleted.");
  };
  const ALL_TABS=[["stats","Stats"],["requests","Requests"],["disputes","Disputes"+(disputedCount>0?" ("+disputedCount+")":"")],["collector_req","Collectors"+(pendingCRs.length>0?" ("+pendingCRs.length+")":"")],["catalog_rev","Catalog"+(pendingCatalog.length>0?" ("+pendingCatalog.length+")":"")],["merge","Merge"+(mergeCandidates.length>0?" ("+mergeCandidates.length+")":"")],["sellers","Sellers"+(pendingSellers.length>0?" ("+pendingSellers.length+")":"")],["listings","Listings"],["designs","Designs"+(pendingDesigns.length>0?" ("+pendingDesigns.length+")":"")],["users","Users"],["beta","Beta Access"+(pendingBeta.length>0?" ("+pendingBeta.length+")":"")],["stories","Stories"],["ai","AI"+(aiEnabled?"":" ⚠")]];
  const MOD_TABS_ALLOWED = new Set(["sellers","catalog_rev","collector_req"]);
  const TABS = isModerator ? ALL_TABS.filter(([k])=>MOD_TABS_ALLOWED.has(k)) : ALL_TABS;

  return (
    <div className="fade-in">
      {viewingId && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.85)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setViewingId(null)}>
          <div style={{position:"relative",maxWidth:700,width:"100%"}} onClick={e=>e.stopPropagation()}>
            <img src={viewingId} alt="ID document" style={{width:"100%",borderRadius:10,boxShadow:"0 24px 64px rgba(0,0,0,.5)"}}/>
            <button onClick={()=>setViewingId(null)} style={{position:"absolute",top:-14,right:-14,width:32,height:32,borderRadius:"50%",background:"#fff",border:"none",fontSize:18,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 2px 8px rgba(0,0,0,.3)"}}>×</button>
          </div>
        </div>
      )}
      <div className="mob-stack-col" style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:20}}>
        <div><span className="sl">{isModerator?"Moderator Panel":"Admin Panel"}</span><h2 style={{fontFamily:"'Playfair Display',serif",fontSize:32,letterSpacing:"-0.02em"}}>The Horological Catalog</h2></div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <span className="tag">{requests.length} req</span>
          <span className="tag tag-grn">{inventory.filter(w=>w.available).length} stock</span>
          <span className="tag tag-gold">{sellers.filter(s=>s.verified).length} verified</span>
          <span className="tag tag-blu">{users.length} users</span>
        </div>
      </div>

      <div className="thc-tabs" style={{marginBottom:22}}>
        {TABS.map(([t,l])=>(
          <button key={t} className={`stab ${tab===t?"active":""}`} onClick={()=>switchTab(t)}>{l.toUpperCase()}</button>
        ))}
      </div>

      {tab==="stats" && (
        <div>
          {/* Key metrics */}
          <div className="mob-2col" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:28}}>
            {[
              ["Users",users.length,"total signups"],
              ["Sellers",sellers.filter(s=>s.verified).length,sellers.length+" total"],
              ["Requests",requests.length,"all time"],
              ["Catalog",catalogEntries.filter(e=>e.status==="approved").length,"approved entries"],
              ["Listings",listings.length,listings.filter(l=>l.verified).length+" live"],
              ["Reviews",reviews.length,"submitted"],
              ["Beta Req",betaRequests.length,betaRequests.filter(r=>r.status==="approved").length+" approved"],
              ["Used Codes",betaRequests.filter(r=>r.used).length,"of "+betaRequests.filter(r=>r.status==="approved").length+" approved"],
            ].map(([label,value,sub])=>(
              <div key={label} className="card" style={{textAlign:"center",padding:"18px 14px"}}>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:30,color:"var(--gold)",lineHeight:1,marginBottom:4}}>{value}</div>
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:3}}>{label}</div>
                <div style={{fontSize:11,color:"var(--ink3)"}}>{sub}</div>
              </div>
            ))}
          </div>

          {/* Beta funnel */}
          <div style={{marginBottom:24}}>
            <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.1em",marginBottom:10}}>BETA FUNNEL</div>
            <div style={{display:"flex",background:"var(--bg2)",borderRadius:10,overflow:"hidden",border:"1px solid var(--bd)"}}>
              {[
                ["Requested",betaRequests.length,"#8A7E74"],
                ["Approved",betaRequests.filter(r=>r.status==="approved").length,"#059669"],
                ["Code Used",betaRequests.filter(r=>r.used).length,"#D97706"],
                ["Signed Up",users.filter(u=>u.createdAt).length,"#2563EB"],
              ].map(([label,n,color],i,arr)=>(
                <div key={label} style={{flex:1,padding:"14px 10px",textAlign:"center",borderRight:i<arr.length-1?"1px solid var(--bd)":"none"}}>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:24,color,lineHeight:1,marginBottom:3}}>{n}</div>
                  <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"var(--ink3)",letterSpacing:"0.06em"}}>{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Request type breakdown */}
          {requests.length>0 && (
            <div style={{marginBottom:24}}>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.1em",marginBottom:10}}>REQUESTS BY TYPE</div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {[["Sourcing",requests.filter(r=>r.type==="sourcing").length,"#D97706"],["Catalog",requests.filter(r=>r.type==="catalog").length,"#059669"],["Authenticity",requests.filter(r=>r.type==="auth").length,"#2563EB"],["Enquiry",requests.filter(r=>r.type==="enquiry").length,"#8B5CF6"]].filter(([,n])=>n>0).map(([label,n,color])=>(
                  <div key={label}>
                    <div style={{display:"flex",justifyContent:"space-between",fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink2)",marginBottom:3}}>
                      <span>{label}</span><span style={{color}}>{n}</span>
                    </div>
                    <div style={{height:4,background:"var(--bg3)",borderRadius:2,overflow:"hidden"}}>
                      <div style={{height:"100%",width:Math.round(n/requests.length*100)+"%",background:color,borderRadius:2}}/>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent signups */}
          {users.length>0 && (
            <div>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.1em",marginBottom:10}}>RECENT SIGNUPS</div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {[...users].sort((a,b)=>(b.createdAt||"").localeCompare(a.createdAt||"")).slice(0,8).map(u=>(
                  <div key={u.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",background:"var(--bg2)",borderRadius:8,gap:12}}>
                    <div style={{minWidth:0}}>
                      <div style={{fontWeight:500,fontSize:13}}>{u.name}</div>
                      <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.email} · {(u.roles||[u.role||"buyer"]).filter(Boolean).join(", ")}</div>
                    </div>
                    <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",flexShrink:0}}>{u.createdAt?.slice(0,10)||"—"}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Note on page-view analytics */}
          <div style={{marginTop:24,padding:"12px 16px",background:"var(--gbg)",border:"1px solid var(--gbd)",borderRadius:8,fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)",lineHeight:1.7}}>
            ⚠ Page-view analytics (visits, bounce rate, sessions) not yet integrated. Add @vercel/analytics or GA4 to track real footfall beyond DB signups.
          </div>
        </div>
      )}

      {tab==="requests" && (
        <div>
          {requests.length===0 && <div style={{textAlign:"center",padding:48,fontFamily:"'DM Mono',monospace",fontSize:12,color:"#8A7E74"}}>No requests.</div>}
          {[...requests].reverse().map(r=>(
            <div key={r.id} className="card" style={{marginBottom:12}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:10}}>
                <div>
                  <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",letterSpacing:"0.12em",marginBottom:4}}>{r.id} · {r.type==="auth"?"AUTH":r.type==="catalog"?"CATALOG":r.type==="enquiry"?"ENQUIRY":"SOURCING"} · {r.amount>0?`₹${r.amount}`:"free"}{r.txnId?` · UTR:${r.txnId}`:""}</div>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:700}}>{r.watch}</div>
                  <div style={{fontSize:12,color:"#4A423A"}}>{r.name} · {r.email}{r.phone?" · "+r.phone:""}</div>
                </div>
                <span className="tag tag-gold">{r.status}</span>
              </div>
              {r.message && <div style={{fontSize:12,color:"#8A7E74",fontStyle:"italic",marginBottom:10}}>"{r.message}"</div>}
              {r.notes && <div style={{fontSize:12,color:"#8A7E74",fontStyle:"italic",marginBottom:10}}>"{r.notes}"</div>}
              {r.type==="enquiry" && <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"#3730A3",marginBottom:10}}>Marketplace enquiry — no payment</div>}
              {(!r.photos || r.photos.length === 0) && (
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--red)",marginBottom:8,letterSpacing:"0.1em"}}>NO PHOTOS</div>
              )}
              <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                <select value={sel[r.id]||""} onChange={e=>setSel(p=>({...p,[r.id]:e.target.value}))} style={{flex:"0 0 160px",fontSize:12}}>
                  <option value="">Update status…</option>{STATUSES.map(s=><option key={s}>{s}</option>)}
                </select>
                <input value={note[r.id]||""} onChange={e=>setNote(p=>({...p,[r.id]:e.target.value}))} placeholder="Note" style={{flex:1,minWidth:120,fontSize:12}}/>
                <button className="btn-ink" style={{padding:"8px 16px",fontSize:12}} onClick={()=>updateStatus(r.id)}>Save</button>
                <button style={{padding:"8px 12px",fontSize:12,background:"none",border:"1px solid var(--rbd)",color:"var(--red)",borderRadius:8,cursor:"pointer"}} onClick={()=>deleteRequest(r)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab==="inventory" && (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(265px,1fr))",gap:12}}>
          {inventory.map(w=>(
            <div key={w.id} className="card">
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                <div><div style={{fontFamily:"'Playfair Display',serif",fontSize:17,fontWeight:700}}>{w.brand} {w.model}</div><div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74"}}>{w.reference} · {w.decade}</div></div>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:16,color:"#B8860B",fontWeight:700}}>₹{w.price?.toLocaleString("en-IN")}</div>
              </div>
              <p style={{fontSize:12,color:"#4A423A",lineHeight:1.6,marginBottom:12}}>{w.description}</p>
              <div style={{display:"flex",gap:8}}>
                <button className="btn-ghost" style={{fontSize:11,padding:"6px 12px",color:w.available?"var(--grn)":"var(--red)",borderColor:w.available?"var(--gnbd)":"var(--rbd)"}} onClick={()=>saveInventory(inventory.map(i=>i.id===w.id?{...i,available:!i.available}:i))}>{w.available?"Mark sold":"Mark available"}</button>
                <button className="btn-ghost" style={{fontSize:11,padding:"6px 12px",color:"var(--red)",borderColor:"var(--rbd)"}} onClick={()=>saveInventory(inventory.filter(i=>i.id!==w.id))}>Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab==="sellers" && (
        <div>
          {/* Invite link generator */}
          <div className="card" style={{marginBottom:20,padding:"16px 20px"}}>
            <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.15em",marginBottom:12}}>GENERATE SELLER INVITE LINK</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr auto",gap:8,alignItems:"flex-end"}}>
              <div><label className="fl" style={{fontSize:10,marginBottom:3}}>Name</label><input value={invLink.name} onChange={e=>setInvLink(p=>({...p,name:e.target.value}))} placeholder="Vikram Sharma" type="text" style={{fontSize:12,padding:"6px 10px"}}/></div>
              <div><label className="fl" style={{fontSize:10,marginBottom:3}}>City</label><input value={invLink.city} onChange={e=>setInvLink(p=>({...p,city:e.target.value}))} placeholder="Mumbai" type="text" style={{fontSize:12,padding:"6px 10px"}}/></div>
              <div><label className="fl" style={{fontSize:10,marginBottom:3}}>Phone / WhatsApp</label><input value={invLink.phone} onChange={e=>setInvLink(p=>({...p,phone:e.target.value}))} placeholder="9876543210" type="tel" style={{fontSize:12,padding:"6px 10px"}}/></div>
              <button className="btn-ink" style={{fontSize:11,padding:"7px 14px",whiteSpace:"nowrap",opacity:invLink.generating?0.6:1}} disabled={invLink.generating} onClick={async()=>{
                setInvLink(p=>({...p,generating:true}));
                try {
                  const token = "inv_" + Math.random().toString(36).slice(2,10) + Math.random().toString(36).slice(2,6);
                  const { error } = await supabase.from("app_settings").insert({ id: token, value: { name: invLink.name, city: invLink.city, phone: invLink.phone, adminInvited: true, createdAt: new Date().toISOString() } });
                  if (error) throw error;
                  const url = `${window.location.origin}${window.location.pathname}?invite=${token}`;
                  await navigator.clipboard.writeText(url).catch(()=>{});
                  toast("Invite link copied! Token is one-time use.");
                } catch(e) { toast("Failed to generate invite — " + e.message); }
                setInvLink(p=>({...p,generating:false}));
              }}>{invLink.generating?"Generating…":"Copy link"}</button>
            </div>
            <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",marginTop:8}}>Invited sellers skip ID verification and are auto-approved as Trusted.</div>
          </div>
          {pendingSellers.length>0 && (
            <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--gold)",letterSpacing:"0.15em",marginBottom:10}}>PENDING APPROVAL ({pendingSellers.length})</div>
          )}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(290px,1fr))",gap:12}}>
          {[...sellers].sort((a,b)=>Number(a.verified)-Number(b.verified)).map(s=>{
            const sellerListings = listings.filter(l=>l.sellerId===s.id);
            const listingPhotos = sellerListings.flatMap(l=>Array.isArray(l.photos)?l.photos:[]).filter(Boolean).slice(0,4);
            return (
            <div key={s.id} className="card" style={{padding:0,overflow:"hidden"}}>
              {/* Avatar / photo header */}
              {s.avatarUrl ? (
                <div style={{height:100,position:"relative",background:"var(--bg2)",overflow:"hidden"}}>
                  <img src={s.avatarUrl} alt={s.name} style={{width:"100%",height:"100%",objectFit:"cover"}} onClick={()=>setViewingId(s.avatarUrl)}/>
                  <div style={{position:"absolute",inset:0,background:"linear-gradient(to bottom,transparent 40%,rgba(0,0,0,.45))"}}/>
                  <div style={{position:"absolute",bottom:8,left:12,color:"#fff"}}>
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:15,fontWeight:700}}>{s.name}</div>
                    <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,opacity:.8}}>{s.handle} · {s.city}</div>
                  </div>
                  {s.verified && <span className="vbadge" style={{position:"absolute",top:8,right:8}}>✦</span>}
                </div>
              ) : (
                <div style={{padding:"14px 16px 0",display:"flex",gap:10,alignItems:"center"}}>
                  <div className="avatar" style={{width:44,height:44,fontSize:14}}>{s.avatar}</div>
                  <div style={{flex:1}}>
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:15,fontWeight:700}}>{s.name}</div>
                    <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74"}}>{s.handle} · {s.city}</div>
                  </div>
                  {s.verified && <span className="vbadge">✦</span>}
                </div>
              )}
              <div style={{padding:"10px 14px 14px"}}>
                <div style={{fontSize:12,color:"#4A423A",marginBottom:10}}>★ {s.rating||"—"} · {s.reviewCount} reviews · {s.sales} sales</div>
                {/* Listing photo thumbnails */}
                {listingPhotos.length>0 && (
                  <div style={{display:"flex",gap:4,marginBottom:10,flexWrap:"wrap"}}>
                    {listingPhotos.map((p,i)=>{
                      const src = typeof p==="string"?p:p?.url||null;
                      return src ? <img key={i} src={src} alt="" onClick={()=>setViewingId(src)} style={{width:52,height:52,objectFit:"cover",borderRadius:6,border:"1px solid var(--bd)",cursor:"pointer"}}/> : null;
                    })}
                    {sellerListings.length>4 && <div style={{width:52,height:52,borderRadius:6,border:"1px solid var(--bd)",background:"var(--bg2)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)"}}>+{sellerListings.length-4}</div>}
                  </div>
                )}
                {s.idDocUrl && (
                  <div style={{marginBottom:10,padding:"8px 10px",background:"var(--bg2)",borderRadius:8,display:"flex",alignItems:"center",gap:10}}>
                    <img src={s.idDocUrl} alt="ID" onClick={()=>setViewingId(s.idDocUrl)} style={{height:44,width:66,borderRadius:5,objectFit:"cover",border:"1px solid var(--bd)",cursor:"pointer"}} title="Click to view full size"/>
                    <div style={{flex:1}}>
                      <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",marginBottom:2}}>GOVT ID SUBMITTED</div>
                      <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:s.idVerified?"#2E7D52":"#B8860B"}}>{s.idVerified?"✓ Verified":"Pending review"}</div>
                    </div>
                    {!s.idVerified && <button className="btn-green" style={{fontSize:10,padding:"5px 10px"}} onClick={async()=>{const{error}=await supabase.from("sellers").update({idVerified:true}).eq("id",s.id);if(error){toast("DB write failed — please retry.");return;}saveSellers(sellers.map(x=>x.id!==s.id?x:{...x,idVerified:true}));if(addNotif){const su=users.find(u=>u.email===s.email||u.sellerId===s.id);addNotif(su?.id||s.id,{type:"system",title:"ID document verified",body:"Your government ID has been reviewed and verified on THC.watch.",emailTo:s.email,emailSubject:"THC.watch — ID document verified"});}}}>Mark verified</button>}
                  </div>
                )}
              <div style={{display:"flex",gap:8,marginBottom:8}}>
                <button onClick={async()=>{
                  if(!s.verified && !s.idVerified){toast("Verify the seller's ID document first.");return;}
                  const nowV=!s.verified;
                  const upd={verified:nowV,verifiedDate:nowV?new Date().toISOString().slice(0,7):null,badge:nowV?"Trusted":null};
                  const{error}=await supabase.from("sellers").update(upd).eq("id",s.id);
                  if(error){toast("DB write failed — please retry.");return;}
                  saveSellers(sellers.map(x=>x.id!==s.id?x:{...x,...upd}));
                  if(addNotif){const su=users.find(u=>u.email===s.email||u.sellerId===s.id);if(nowV)addNotif(su?.id||s.id,{type:"approval",title:"Your seller account is now Trusted ✦",body:"Congratulations — your account has been verified on THC.watch. You can now list watches and accept sourcing requests.",link:{view:"seller-dash"},emailTo:s.email,emailSubject:"Your THC.watch seller account is now verified"});else addNotif(su?.id||s.id,{type:"system",title:"Seller verification revoked",body:"Your Trusted seller status on THC.watch has been removed. Contact hello@thc.watch if you have questions.",emailTo:s.email,emailSubject:"THC.watch — seller verification revoked"});}}} className={s.verified?"btn-ghost":"btn-gold"} style={{fontSize:11,padding:"7px 12px",flex:1,color:s.verified?"var(--red)":"",borderColor:s.verified?"var(--rbd)":""}}>
                  {s.verified?"Revoke Trust":"Mark Trusted ✦"}
                </button>
                {!s.verified && <button className="btn-gold" style={{fontSize:11,padding:"7px 12px",opacity:s.idVerified?1:0.5,cursor:s.idVerified?"pointer":"not-allowed"}} onClick={async()=>{if(!s.idVerified){toast("Verify the seller's ID document first.");return;}const upd={verified:true,badge:"Top Seller",verifiedDate:new Date().toISOString().slice(0,7)};const{error}=await supabase.from("sellers").update(upd).eq("id",s.id);if(error){toast("DB write failed — please retry.");return;}saveSellers(sellers.map(x=>x.id!==s.id?x:{...x,...upd}));if(addNotif){const su=users.find(u=>u.email===s.email||u.sellerId===s.id);addNotif(su?.id||s.id,{type:"approval",title:"You're a Top Seller ✦",body:"Your account has been given Top Seller status on THC.watch.",link:{view:"seller-dash"},emailTo:s.email,emailSubject:"You're now a Top Seller on THC.watch"});}}}>Top Seller</button>}
              </div>
              {!s.verified && (
                <div>
                  {resubmitOpen[s.id] === undefined ? (
                    <button className="btn-ghost" style={{fontSize:11,padding:"7px 12px",width:"100%",color:"var(--gold)",borderColor:"var(--gold)"}}
                      onClick={()=>setResubmitOpen(p=>({...p,[s.id]:s.resubmitNote||""}))}>
                      ↩ Request Resubmission
                    </button>
                  ) : (
                    <div style={{background:"#FFF8E7",border:"1px solid #F0D080",borderRadius:8,padding:"10px 12px"}}>
                      <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#7A5C00",marginBottom:6}}>NOTE TO SELLER (optional)</div>
                      <textarea
                        value={resubmitOpen[s.id]}
                        onChange={e=>setResubmitOpen(p=>({...p,[s.id]:e.target.value}))}
                        rows={2}
                        placeholder="e.g. Your ID photo was unclear — please re-upload a clearer photo."
                        style={{width:"100%",fontSize:12,marginBottom:8,resize:"vertical"}}
                      />
                      <div style={{display:"flex",gap:6}}>
                        <button className="btn-ink" style={{fontSize:11,padding:"6px 12px",flex:1}} onClick={async()=>{
                          const noteText = resubmitOpen[s.id];
                          const{error}=await supabase.from("sellers").update({resubmitRequested:true,resubmitNote:noteText}).eq("id",s.id);
                          if(error){toast("DB write failed — please retry.");return;}
                          saveSellers(sellers.map(x=>x.id!==s.id?x:{...x,resubmitRequested:true,resubmitNote:noteText}));
                          const su=users.find(u=>u.email===s.email||u.sellerId===s.id);
                          if(addNotif) addNotif(su?.id||s.id,{type:"system",title:"Action required — resubmit your seller application",body:`Our team has reviewed your seller application and needs some changes.${noteText?" Note: "+noteText:""} Please log in and resubmit your details.`,link:{view:"market",tab:"apply"},emailTo:s.email,emailSubject:"THC.watch — please resubmit your seller application"});
                          setResubmitOpen(p=>({...p,[s.id]:undefined}));
                          toast("Resubmission requested — seller notified.");
                        }}>Send</button>
                        <button className="btn-ghost" style={{fontSize:11,padding:"6px 12px"}} onClick={()=>setResubmitOpen(p=>({...p,[s.id]:undefined}))}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          );
        })}
        </div>
        </div>
      )}

      {tab==="listings" && (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(270px,1fr))",gap:12}}>
          {listings.map(l=>{
            const s=sellers.find(x=>x.id===l.sellerId);
            return (
              <div key={l.id} className="card">
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                  <div><div style={{fontFamily:"'Playfair Display',serif",fontSize:17,fontWeight:700}}>{l.brand} {l.model}</div><div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74"}}>{l.decade} · by {s?.name||"unknown"}</div></div>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:16,color:"#B8860B",fontWeight:700}}>₹{l.price?.toLocaleString("en-IN")}</div>
                </div>
                <p style={{fontSize:12,color:"#4A423A",lineHeight:1.6,marginBottom:12}}>{l.description}</p>
                <button onClick={()=>saveListings(listings.map(x=>x.id!==l.id?x:{...x,verified:!x.verified}))} className={l.verified?"btn-ghost":"btn-gold"} style={{fontSize:11,padding:"7px 12px",width:"100%",color:l.verified?"var(--red)":"",borderColor:l.verified?"var(--rbd)":""}}>
                  {l.verified?"Remove Verification":"Verify Listing ✦"}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {tab==="users" && (
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)"}}>{users.length} user{users.length!==1?"s":""}</div>
            <button className="btn-ghost" style={{fontSize:11,padding:"5px 14px"}} onClick={async()=>{
              const res = await supabase.from("users").select("id,name,email,role,roles,phone,sellerId,createdAt,status,suspended,bio,phoneVerified,collectorId,avatar,restrictedActions,buyerRating,buyerFlags,addressLine1,addressLine2,addressCity,addressState,addressPostalCode,addressCountry");
              if (res.data) { saveUsers(res.data); toast(`${res.data.length} users loaded.`); }
            }}>↻ Refresh</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(290px,1fr))",gap:12}}>
            {users.map(u=>{
              const uRoles = [...new Set([...(u.roles||[]), u.role].filter(Boolean))];
              const uIsAdmin = uRoles.includes("admin");
              const uIsModerator = uRoles.includes("moderator");
              const uIsSeller = uRoles.includes("seller");
              const uIsCollector = uRoles.includes("collector");
              const isSelf = u.id === session?.user?.id;
              const isAwaitingDelete = deleteConfirm === u.id;
              const toggleAdmin = async () => {
                const next = uIsAdmin ? uRoles.filter(r=>r!=="admin") : [...uRoles,"admin"];
                const patch = {id:u.id, roles:next, role:next[0]||"buyer"};
                try { await upsertOne(SK.users, patch); } catch { toast("Save failed — please retry."); return; }
                saveUsers(users.map(x=>x.id!==u.id?x:{...x,...patch}));
                if (addNotif) {
                  if (uIsAdmin) addNotif(u.id, {type:"system", title:"Admin access revoked", body:"Your admin access on THC.watch has been removed.", emailTo:u.email, emailSubject:"THC.watch — admin access removed"});
                  else addNotif(u.id, {type:"system", title:"Admin access granted", body:"You've been granted admin access on THC.watch.", emailTo:u.email, emailSubject:"THC.watch — you're now an admin"});
                }
              };
              const toggleModerator = async () => {
                const next = uIsModerator ? uRoles.filter(r=>r!=="moderator") : [...uRoles,"moderator"];
                const patch = {id:u.id, roles:next, role:next[0]||"buyer"};
                if (uIsModerator) patch.moderatorLang = null;
                try { await upsertOne(SK.users, patch); } catch { toast("Save failed — please retry."); return; }
                saveUsers(users.map(x=>x.id!==u.id?x:{...x,...patch}));
                addNotif?.(u.id, {type:"system", title:uIsModerator?"Moderator access revoked":"Moderator access granted", body:uIsModerator?"Your moderator access on THC.watch has been removed.":"You've been made a moderator on THC.watch. You can now approve sellers and catalog entries.", emailTo:u.email, emailSubject:uIsModerator?"THC.watch — moderator access removed":"THC.watch — you're now a moderator"});
              };
              const setModeratorLang = async (langCode) => {
                const patch = {id:u.id, moderatorLang: langCode || null};
                try { await upsertOne(SK.users, patch); } catch { toast("Save failed."); return; }
                saveUsers(users.map(x=>x.id!==u.id?x:{...x,...patch}));
              };
              const toggleSuspend = async () => {
                const nowSuspended = !u.suspended;
                const patch = {id:u.id, suspended:nowSuspended};
                try { await upsertOne(SK.users, patch); } catch { toast("Save failed — please retry."); return; }
                saveUsers(users.map(x=>x.id!==u.id?x:{...x,...patch}));
                if (addNotif) {
                  if (nowSuspended) addNotif(u.id, {type:"system", title:"Account suspended", body:"Your THC.watch account has been suspended. Contact hello@thc.watch if you think this is a mistake.", emailTo:u.email, emailSubject:"THC.watch — account suspended"});
                  else addNotif(u.id, {type:"system", title:"Account reinstated", body:"Your THC.watch account has been reinstated. You can log in and continue as normal.", emailTo:u.email, emailSubject:"THC.watch — account reinstated"});
                }
              };
              const revokeRole = async (role) => {
                const next = uRoles.filter(r=>r!==role);
                const patch = {id:u.id, roles:next, role:next[0]||"buyer"};
                try { await upsertOne(SK.users, patch); } catch { toast("Save failed — please retry."); return; }
                saveUsers(users.map(x=>x.id!==u.id?x:{...x,...patch}));
                if (addNotif) {
                  const label = role === "seller" ? "Seller" : "Collector";
                  addNotif(u.id, {type:"system", title:`${label} access revoked`, body:`Your ${label} role on THC.watch has been removed by an admin. Contact hello@thc.watch if you have questions.`, emailTo:u.email, emailSubject:`THC.watch — ${label.toLowerCase()} access revoked`});
                }
              };
              const removeFromPlatform = async () => {
                const patch = {id:u.id, removedFromPlatform:true, suspended:true, preRemovalRoles:uRoles, roles:["buyer"], role:"buyer"};
                try { await upsertOne(SK.users, patch); } catch { toast("Save failed — please retry."); return; }
                saveUsers(users.map(x=>x.id!==u.id?x:{...x,...patch}));
                addNotif?.(u.id, {type:"system", title:"Account removed", body:"Your THC.watch account has been removed from the platform. Contact hello@thc.watch to appeal.", emailTo:u.email, emailSubject:"THC.watch — account removed"});
                toast(`${u.name} removed from platform.`);
              };
              const restoreUser = async () => {
                const restoredRoles = u.preRemovalRoles || ["buyer"];
                const patch = {id:u.id, removedFromPlatform:false, suspended:false, roles:restoredRoles, role:restoredRoles[0]||"buyer"};
                try { await upsertOne(SK.users, patch); } catch { toast("Save failed — please retry."); return; }
                saveUsers(users.map(x=>x.id!==u.id?x:{...x,...patch}));
                addNotif?.(u.id, {type:"system", title:"Account restored", body:"Your THC.watch account has been restored. You can log in and continue as normal.", emailTo:u.email, emailSubject:"THC.watch — account restored"});
                toast(`${u.name} restored.`);
              };
              const resendInvite = async () => {
                try {
                  const role = (u.roles||[u.role]||["buyer"]).filter(Boolean)[0] || "buyer";
                  const firstName = u.name?.split(" ")[0] || "there";
                  await supabase.functions.invoke("send-email", { body: {
                    to: u.email,
                    subject: "Welcome (again) to THC.watch — here's everything you can do",
                    html: capabilitiesHtml(role, firstName),
                  }});
                  const now = new Date().toISOString();
                  await upsertOne(SK.users, {id:u.id, lastInviteEmailAt:now});
                  saveUsers(users.map(x=>x.id!==u.id?x:{...x,lastInviteEmailAt:now}));
                  toast(`Invite sent to ${u.email}`);
                } catch { toast("Failed to send — check edge function logs."); }
              };
              const deleteUser = async () => {
                setDeleteConfirm(null);
                // Remove seller profile + listings if they exist
                if (u.sellerId) {
                  await deleteFromDB(SK.sellers, u.sellerId);
                  const sellerListings = listings.filter(l=>l.sellerId===u.sellerId);
                  for (const l of sellerListings) await deleteFromDB(SK.listings, l.id);
                  saveListings(listings.filter(l=>l.sellerId!==u.sellerId));
                  saveSellers(sellers.filter(s=>s.id!==u.sellerId));
                }
                // Remove collector request if any
                const cr = (collectorRequests||[]).find(r=>r.userId===u.id);
                if (cr) { await deleteFromDB("collector_requests", cr.id); saveCRs((collectorRequests||[]).filter(r=>r.userId!==u.id)); }
                // Remove the user row
                await deleteFromDB(SK.users, u.id);
                saveUsers(users.filter(x=>x.id!==u.id));
                toast(`${u.name} deleted.`);
              };
              return (
              <div key={u.id} className="card" style={{opacity:u.removedFromPlatform?0.55:u.suspended?0.7:1,outline:u.removedFromPlatform?"2px solid #111118":u.suspended?"2px solid var(--rbd)":"none"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:17,fontWeight:700,display:"flex",alignItems:"center",gap:6}}>
                      {u.name}
                      {u.removedFromPlatform && <span className="tag" style={{fontSize:8,background:"#111118",color:"#fff",borderColor:"#111118"}}>REMOVED</span>}
                      {!u.removedFromPlatform && u.suspended && <span className="tag tag-red" style={{fontSize:8}}>SUSPENDED</span>}
                      {uIsModerator && <span className="tag" style={{fontSize:8,background:"#EDE9FE",color:"#6D28D9",borderColor:"#C4B5FD"}}>{u.moderatorLang ? `MOD·${u.moderatorLang.toUpperCase()}` : "MOD"}</span>}
                    </div>
                    <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)",marginTop:2}}>{u.email}</div>
                    {u.phone && <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)"}}>{u.phone}</div>}
                  </div>
                  <span className="role-pill" style={{flexShrink:0,background:uIsAdmin?"var(--rbg)":uIsSeller?"var(--gnbg)":uIsCollector?"var(--gbg)":"var(--bbg)",color:uIsAdmin?"var(--red)":uIsSeller?"var(--grn)":uIsCollector?"var(--gold)":"var(--blu)"}}>{uRoles.join("+")}</span>
                </div>
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",marginBottom:8}}>{u.createdAt ? `Joined ${new Date(u.createdAt).toLocaleDateString("en-IN")}` : ""}</div>
                {/* Onboarding status tracker */}
                {(() => {
                  const steps = [
                    { label:"Joined", done:!!u.createdAt },
                    { label:"Invited", done:!!u.lastInviteEmailAt, sub: u.lastInviteEmailAt ? timeAgo(u.lastInviteEmailAt) : null },
                    { label:"Has Role", done: uRoles.some(r=>r!=="buyer") || uIsSeller || uIsCollector || uIsAdmin },
                    { label:"Seller ✦", done: !!u.sellerId },
                  ];
                  return (
                    <div style={{display:"flex",gap:4,marginBottom:10,alignItems:"center"}}>
                      {steps.map((s,i)=>(
                        <React.Fragment key={s.label}>
                          <div style={{textAlign:"center"}}>
                            <div style={{width:8,height:8,borderRadius:"50%",background:s.done?"var(--grn)":"var(--bg3)",border:`1px solid ${s.done?"var(--grn)":"var(--bd)"}`,margin:"0 auto 2px"}}/>
                            <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:s.done?"var(--ink2)":"var(--ink3)",whiteSpace:"nowrap"}}>{s.label}</div>
                            {s.sub && <div style={{fontFamily:"'DM Mono',monospace",fontSize:7,color:"var(--ink3)"}}>{s.sub}</div>}
                          </div>
                          {i<steps.length-1 && <div style={{flex:1,height:1,background:"var(--bd)",marginBottom:10,minWidth:6}}/>}
                        </React.Fragment>
                      ))}
                    </div>
                  );
                })()}
                {/* Credit balance + manual adjustment */}
                <CreditAdjust u={u} addCredit={addCredit} toast={toast} saveUsers={saveUsers} users={users}/>
                {!isSelf && (
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    <button onClick={toggleAdmin} className="btn-ghost" style={{fontSize:10,padding:"5px 12px",color:uIsAdmin?"var(--red)":"var(--accent)",borderColor:uIsAdmin?"var(--rbd)":"var(--bbd)"}}>
                      {uIsAdmin?"Revoke Admin":"Make Admin"}
                    </button>
                    <button onClick={toggleModerator} className="btn-ghost" style={{fontSize:10,padding:"5px 12px",color:uIsModerator?"#6D28D9":"#7C3AED",borderColor:"#C4B5FD"}}>
                      {uIsModerator?"Revoke Mod":"Make Mod"}
                    </button>
                    {uIsModerator && (
                      <select value={u.moderatorLang||""} onChange={e=>setModeratorLang(e.target.value)} style={{fontSize:10,padding:"5px 10px",borderRadius:8,border:"1px solid #C4B5FD",background:"#EDE9FE",color:"#6D28D9",cursor:"pointer"}}>
                        <option value="">Lang: Any</option>
                        {LANG_OPTIONS.filter(l=>l.code!=="en").map(l=>(
                          <option key={l.code} value={l.code}>{l.label} ({l.native})</option>
                        ))}
                      </select>
                    )}
                    <button onClick={toggleSuspend} className="btn-ghost" style={{fontSize:10,padding:"5px 12px",color:u.suspended?"var(--grn)":"var(--red)",borderColor:u.suspended?"var(--gnbd)":"var(--rbd)"}}>
                      {u.suspended?"Unsuspend":"Suspend"}
                    </button>
                    <button onClick={resendInvite} className="btn-ghost" style={{fontSize:10,padding:"5px 12px",color:"var(--blu)",borderColor:"var(--bbd)"}}>Resend Invite</button>
                    {uIsSeller && !u.removedFromPlatform && <button onClick={()=>revokeRole("seller")} className="btn-ghost" style={{fontSize:10,padding:"5px 12px",color:"var(--red)",borderColor:"var(--rbd)"}}>Revoke Seller</button>}
                    {uIsCollector && !u.removedFromPlatform && <button onClick={()=>revokeRole("collector")} className="btn-ghost" style={{fontSize:10,padding:"5px 12px",color:"var(--red)",borderColor:"var(--rbd)"}}>Revoke Collector</button>}
                    {u.removedFromPlatform
                      ? <button onClick={restoreUser} className="btn-ghost" style={{fontSize:10,padding:"5px 12px",color:"var(--grn)",borderColor:"var(--gnbd)"}}>↩ Restore User</button>
                      : <button onClick={removeFromPlatform} className="btn-ghost" style={{fontSize:10,padding:"5px 12px",color:"#111118",borderColor:"#C4C4CC"}}>Remove from Platform</button>
                    }
                    {!isAwaitingDelete
                      ? <button onClick={()=>setDeleteConfirm(u.id)} className="btn-ghost" style={{fontSize:10,padding:"5px 12px",color:"var(--red)",borderColor:"var(--rbd)",marginLeft:"auto"}}>Delete User</button>
                      : <div style={{display:"flex",alignItems:"center",gap:6,marginLeft:"auto",background:"#FFF1F2",border:"1px solid var(--rbd)",borderRadius:8,padding:"5px 10px"}}>
                          <span style={{fontSize:10,color:"var(--red)",fontWeight:600}}>Delete permanently?</span>
                          <button onClick={deleteUser} className="btn-ghost" style={{fontSize:10,padding:"3px 10px",background:"var(--red)",color:"#fff",borderColor:"var(--red)"}}>Yes, delete</button>
                          <button onClick={()=>setDeleteConfirm(null)} className="btn-ghost" style={{fontSize:10,padding:"3px 10px"}}>Cancel</button>
                        </div>
                    }
                  </div>
                )}
                {isSelf && <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)"}}>— your account</div>}
              </div>
              );
            })}
          </div>
        </div>
      )}

      {tab==="add" && (
        <div style={{maxWidth:540}}>
          <div className="mob-1col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
            <div><label className="fl">Brand *</label><select value={nw.brand} onChange={e=>uNw("brand",e.target.value)}><option value="">Select</option><option>HMT</option><option>Allwyn</option><option>Ricoh</option></select></div>
            <div><label className="fl">Model *</label><input value={nw.model} onChange={e=>uNw("model",e.target.value)} placeholder="Pilot…"/></div>
            <div><label className="fl">Reference</label><input value={nw.reference} onChange={e=>uNw("reference",e.target.value)}/></div>
            <div><label className="fl">Decade</label><select value={nw.decade} onChange={e=>uNw("decade",e.target.value)}><option value="">Select</option>{["1960s","1970s","1980s","1990s"].map(d=><option key={d}>{d}</option>)}</select></div>
            <div><label className="fl">Condition</label><select value={nw.condition} onChange={e=>uNw("condition",e.target.value)}><option value="">Select</option>{["Mint","Excellent","Good","Fair"].map(c=><option key={c}>{c}</option>)}</select></div>
            <div><label className="fl">Price ₹ *</label><input value={nw.price} onChange={e=>uNw("price",e.target.value)} placeholder="2500"/></div>
            <div style={{gridColumn:"1/-1"}}><label className="fl">Description</label><textarea value={nw.description} onChange={e=>uNw("description",e.target.value)} rows={3} placeholder="Honest condition report."/></div>
            <div style={{gridColumn:"1/-1"}}><label className="fl">Tags (comma-separated)</label><input value={nw.tags} onChange={e=>uNw("tags",e.target.value)} placeholder="hand-wound, black dial…"/></div>
            <div style={{display:"flex",alignItems:"center",gap:10}}><input type="checkbox" checked={nw.available} onChange={e=>uNw("available",e.target.checked)} style={{width:"auto",accentColor:"#1A1612"}}/><label className="fl" style={{marginBottom:0}}>Available for sale</label></div>
          </div>
          <button className="btn-ink" onClick={()=>{
            if(!nw.brand||!nw.model||!nw.price){toast("Brand, model, price required.");return;}
            saveInventory([...inventory,{...nw,id:"w"+Date.now(),price:parseInt(nw.price),tags:nw.tags.split(",").map(t=>t.trim()).filter(Boolean)}]);
            setNw({brand:"",model:"",reference:"",decade:"",condition:"",price:"",description:"",tags:"",available:true});
            toast("Added."); setTab("inventory");
          }}>Add to inventory →</button>
        </div>
      )}

      {tab==="disputes" && (
        <div>
          {requests.filter(r=>r.status==="Disputed"||r.dispute).length===0
            ? <div style={{fontFamily:"'DM Mono',monospace",fontSize:12,color:"var(--ink3)",padding:"40px 0"}}>No active disputes.</div>
            : requests.filter(r=>r.status==="Disputed"||r.dispute).map(r=>{
                const seller = sellers.find(s=>s.id===r.sellerClaim?.sellerId);
                return (
                  <div key={r.id} className="card" style={{marginBottom:16,borderLeft:"3px solid var(--red)"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                      <div>
                        <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",marginBottom:3}}>{r.id}</div>
                        <div style={{fontFamily:"'Playfair Display',serif",fontSize:18}}>{r.watch}</div>
                      </div>
                      <span className="tag tag-red">Disputed</span>
                    </div>
                    <div className="mob-1col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14,fontSize:12,color:"var(--ink2)"}}>
                      <div><strong>Buyer:</strong> {r.name} · {r.email}</div>
                      <div><strong>Seller:</strong> {seller?.name||r.sellerClaim?.sellerName||"—"}</div>
                    </div>
                    {r.dispute && (
                      <div style={{background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:8,padding:"12px 14px",marginBottom:14}}>
                        <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--red)",marginBottom:6}}>DISPUTE REASON</div>
                        <div style={{fontSize:13,color:"var(--ink2)",lineHeight:1.6}}>{r.dispute.reason}</div>
                        {r.dispute.raisedAt && <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",marginTop:6}}>Raised {new Date(r.dispute.raisedAt).toLocaleDateString("en-IN")}</div>}
                      </div>
                    )}
                    <div className="mob-1col" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
                      {r.boxingVideoUrl && <div><div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",marginBottom:4}}>BOXING VIDEO</div><video src={r.boxingVideoUrl} controls style={{width:"100%",borderRadius:8,maxHeight:160}}/></div>}
                      {r.unboxingVideoUrl && <div><div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",marginBottom:4}}>UNBOXING VIDEO</div><video src={r.unboxingVideoUrl} controls style={{width:"100%",borderRadius:8,maxHeight:160}}/></div>}
                    </div>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                      <select value={sel[r.id]||""} onChange={e=>setSel(p=>({...p,[r.id]:e.target.value}))} style={{flex:"0 0 200px",fontSize:12}}>
                        <option value="">Resolve as…</option>
                        <option value="Dispute Resolved — Buyer">Refund Buyer</option>
                        <option value="Dispute Resolved — Seller">Release to Seller</option>
                        <option value="Completed">Mark Completed (no dispute)</option>
                      </select>
                      <input value={note[r.id]||""} onChange={e=>setNote(p=>({...p,[r.id]:e.target.value}))} placeholder="Resolution note for buyer" style={{flex:1,minWidth:120,fontSize:12}}/>
                      <button className="btn-ink" style={{fontSize:12,padding:"8px 16px",background:"var(--red)",borderColor:"var(--red)"}} onClick={()=>updateStatus(r.id)}>Resolve</button>
                    </div>
                  </div>
                );
              })
          }
        </div>
      )}

      {tab==="collector_req" && (
        <div>
          {collectorRequests.length===0 && <div style={{textAlign:"center",padding:48,fontFamily:"'DM Mono',monospace",fontSize:12,color:"#8A7E74"}}>No collector requests yet.</div>}
          {[...collectorRequests].reverse().map(cr=>(
            <div key={cr.id} className="card" style={{marginBottom:12}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:10}}>
                <div>
                  <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",letterSpacing:"0.12em",marginBottom:4}}>{cr.createdAt?.slice(0,10)} · {cr.id}</div>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:700}}>{cr.name}</div>
                  <div style={{fontSize:12,color:"#4A423A"}}>{cr.email}</div>
                </div>
                <span className={`tag ${cr.status==="approved"?"tag-grn":cr.status==="rejected"?"tag-red":"tag-gold"}`}>{cr.status}</span>
              </div>
              {cr.reason && <div style={{fontSize:13,color:"#4A423A",marginBottom:12,fontStyle:"italic"}}>"{cr.reason}"</div>}
              {cr.idDocUrl && (
                <div style={{marginBottom:12,padding:"8px 10px",background:"var(--bg2)",borderRadius:8,display:"flex",alignItems:"center",gap:10}}>
                  <img src={cr.idDocUrl} alt="ID" onClick={()=>setViewingId(cr.idDocUrl)} style={{height:40,borderRadius:5,objectFit:"cover",border:"1px solid var(--bd)",cursor:"pointer"}} title="Click to view full size"/>
                  <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74"}}>GOVT ID SUBMITTED</div>
                </div>
              )}
              {cr.status==="pending" && (
                <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                  <input value={crNote[cr.id]||""} onChange={e=>setCRNote(p=>({...p,[cr.id]:e.target.value}))} placeholder="Note (optional)" style={{flex:1,minWidth:120,fontSize:12}}/>
                  <button className="btn-green" style={{padding:"8px 16px",fontSize:12}} onClick={()=>approveCollector(cr)}>Approve ✓</button>
                  <button className="btn-ghost" style={{padding:"8px 16px",fontSize:12,color:"var(--red)",borderColor:"var(--rbd)"}} onClick={()=>rejectCollector(cr)}>Reject</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab==="catalog_rev" && (
        <div>
          <div style={{display:"flex",gap:10,marginBottom:14,alignItems:"center",flexWrap:"wrap"}}>
            <input placeholder="Search brand, model…" onChange={e=>setNote(p=>({...p,_catQ:e.target.value}))} value={note._catQ||""} style={{flex:1,minWidth:140,fontSize:12,padding:"8px 12px"}}/>
            <div style={{display:"flex",gap:4}}>
              {["all","pending","approved","hidden"].map(s=>(
                <button key={s} onClick={()=>setNote(p=>({...p,_catF:s}))} style={{padding:"6px 10px",fontSize:9,fontFamily:"'DM Mono',monospace",borderRadius:6,border:"1px solid var(--bd)",background:(note._catF||"all")===s?"#111118":"transparent",color:(note._catF||"all")===s?"#fff":"var(--ink3)",cursor:"pointer"}}>{s.toUpperCase()}</button>
              ))}
            </div>
          </div>
          {catalogEntries.length===0 && <div style={{textAlign:"center",padding:48,fontFamily:"'DM Mono',monospace",fontSize:12,color:"#8A7E74"}}>No catalog submissions yet.</div>}
          {[...catalogEntries]
            .filter(e=>{
              const f=note._catF||"all";
              const q=(note._catQ||"").toLowerCase();
              const statusOk = f==="all"||(f==="approved"?e.status==="approved":f==="hidden"?e.status==="hidden":f==="pending"?e.status==="pending":true);
              const searchOk = !q||`${e.brand} ${e.model} ${e.collectorName||""}`.toLowerCase().includes(q);
              return statusOk && searchOk;
            })
            .sort((a,b)=>a.status==="pending"?-1:1)
            .map(entry=>(
            <div key={entry.id} className="card" style={{marginBottom:14,opacity:entry.status==="hidden"?0.6:1}}>
              <div style={{display:"flex",gap:14,marginBottom:12}}>
                {entry.photos?.length>0 && (
                  <div style={{display:"flex",gap:8,flexShrink:0}}>
                    {entry.photos.slice(0,3).map((p,i)=>{
                      const src = typeof p==="string"?p:p?.url;
                      return src ? <img key={i} src={src} alt="" style={{width:72,height:72,objectFit:"cover",borderRadius:8,border:"1.5px solid var(--bd)"}}/> : null;
                    })}
                  </div>
                )}
                <div style={{flex:1}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                    <div>
                      <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",letterSpacing:"0.1em",marginBottom:3}}>{entry.brand} · {entry.decade} · by {entry.collectorName}</div>
                      <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:700}}>{entry.brand} {entry.model}</div>
                      {entry.reference && <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74"}}>Ref. {entry.reference}</div>}
                    </div>
                    <div style={{textAlign:"right"}}>
                      <span className={`tag ${entry.status==="approved"?"tag-grn":entry.status==="hidden"?"tag-red":entry.status==="rejected"?"tag-red":"tag-gold"}`}>{entry.status==="hidden"?"Hidden":entry.status}</span>
                      {entry.price && <div style={{fontFamily:"'Playfair Display',serif",fontSize:16,color:"#B8860B",marginTop:6}}>₹{entry.price?.toLocaleString("en-IN")}</div>}
                    </div>
                  </div>
                  <p style={{fontSize:12.5,color:"#4A423A",lineHeight:1.6}}>{entry.description}</p>
                </div>
              </div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap",borderTop:"1px solid var(--bd)",paddingTop:10}}>
                {entry.status==="pending" && <>
                  <input value={crNote[entry.id]||""} onChange={e=>setCRNote(p=>({...p,[entry.id]:e.target.value}))} placeholder="Rejection reason (optional)" style={{flex:1,minWidth:160,fontSize:12}}/>
                  <button className="btn-green" style={{padding:"7px 14px",fontSize:12}} onClick={()=>approveCatalog(entry)}>Approve ✓</button>
                  <button className="btn-ghost" style={{padding:"7px 14px",fontSize:12,color:"var(--red)",borderColor:"var(--rbd)"}} onClick={()=>rejectCatalog(entry)}>Reject</button>
                </>}
                {entry.status==="approved" && <>
                  <button className="btn-ghost" style={{padding:"7px 14px",fontSize:12,color:"var(--ink3)"}} onClick={()=>hideCatalog(entry)}>Hide from catalog</button>
                </>}
                {entry.status==="hidden" && <>
                  <button className="btn-green" style={{padding:"7px 14px",fontSize:12}} onClick={()=>unhideCatalog(entry)}>Restore ✓</button>
                </>}
                <button className="btn-ghost" style={{padding:"7px 14px",fontSize:12,color:"var(--red)",borderColor:"var(--rbd)",marginLeft:"auto"}} onClick={()=>deleteCatalogEntry(entry)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab==="merge" && (
        <div>
          <div style={{marginBottom:20}}>
            <p style={{fontSize:13,color:"#4A423A",lineHeight:1.7,maxWidth:560}}>
              These catalog entries share the same brand + model. Merging pools all their photos into the first (collector/sale) entry and re-links any buyer requests. Sale posts and request posts are unchanged.
            </p>
            <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",marginTop:6}}>
              TODO: wire up AI cluster-similarity to also catch fuzzy variants (see groupCatalogEntries).
            </div>
          </div>

          {mergeCandidates.length===0 && (
            <div style={{textAlign:"center",padding:48,fontFamily:"'DM Mono',monospace",fontSize:12,color:"#8A7E74"}}>No merge candidates — catalog looks clean.</div>
          )}

          {mergeCandidates.map((group,gi)=>(
            <div key={gi} className="card" style={{marginBottom:16}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
                <div>
                  <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",letterSpacing:"0.1em",marginBottom:4}}>{group.entries.length} ENTRIES{group.wms.length>1?` · ${group.wms.length} MODELS`:""} · SAME WATCH</div>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:22}}>{group.entries[0].brand} {group.entries[0].model}</div>
                </div>
                <button className="btn-gold" style={{padding:"9px 18px",fontSize:12}} onClick={()=>handleMerge(group)}>
                  Merge & pool photos →
                </button>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {group.entries.map((e,i)=>(
                  <div key={e.id} style={{display:"flex",gap:12,alignItems:"center",background:"var(--bg2)",borderRadius:8,padding:"10px 14px"}}>
                    {e.photos?.[0] && <img src={e.photos[0]} alt="" style={{width:48,height:48,objectFit:"cover",borderRadius:6,border:"1.5px solid var(--bd)",flexShrink:0}}/>}
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:2}}>
                        {i===0 && <span style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"#fff",background:"#1A1612",padding:"1px 6px",borderRadius:2}}>MASTER</span>}
                        <span className={`tag ${e.status==="approved"?"tag-grn":e.status==="pending"?"tag-gold":"tag-red"}`} style={{fontSize:8}}>{e.status}</span>
                        {e.entryType && <span style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"#8A7E74"}}>{e.entryType.toUpperCase()}</span>}
                      </div>
                      <div style={{fontSize:12,color:"#4A423A",fontFamily:"'DM Mono',monospace"}}>{e.collectorName} · {e.createdAt?.slice(0,10)}</div>
                      {e.photos?.length>0 && <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",marginTop:2}}>{e.photos.length} photo{e.photos.length>1?"s":""}</div>}
                    </div>
                    {e.price && <div style={{fontFamily:"'Playfair Display',serif",fontSize:15,color:"#B8860B",flexShrink:0}}>₹{e.price.toLocaleString("en-IN")}</div>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab==="designs" && (
        <div>
          <div style={{background:"var(--bg2)",borderRadius:12,padding:"12px 18px",marginBottom:18,fontSize:13,color:"var(--ink2)",lineHeight:1.7}}>
            Watch Labs submissions. Approve to make them visible to collectors for pre-ordering. When a design hits its minimum-order threshold, it auto-flips to <strong>Funded</strong> — you can then advance to <strong>In production</strong> or <strong>Produced</strong>.
          </div>
          <div style={{display:"flex",gap:10,marginBottom:14,alignItems:"center",flexWrap:"wrap"}}>
            <input placeholder="Search design name, designer…" onChange={e=>setNote(p=>({...p,_dQ:e.target.value}))} value={note._dQ||""} style={{flex:1,minWidth:140,fontSize:12,padding:"8px 12px"}}/>
            <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
              {["all","pending","approved","funded","in_production","produced","rejected"].map(s=>(
                <button key={s} onClick={()=>setNote(p=>({...p,_dF:s}))} style={{padding:"6px 10px",fontSize:9,fontFamily:"'DM Mono',monospace",borderRadius:6,border:"1px solid var(--bd)",background:(note._dF||"all")===s?"#111118":"transparent",color:(note._dF||"all")===s?"#fff":"var(--ink3)",cursor:"pointer"}}>{s.replace("_"," ").toUpperCase()}</button>
              ))}
            </div>
          </div>
          {(watchDesigns||[]).length===0 && <div style={{textAlign:"center",padding:48,fontFamily:"'DM Mono',monospace",fontSize:12,color:"var(--ink3)"}}>No design submissions yet.</div>}
          {[...(watchDesigns||[])]
            .filter(d=>{
              const f=note._dF||"all";
              const q=(note._dQ||"").toLowerCase();
              const statusOk = f==="all" || d.status===f;
              const searchOk = !q || `${d.name} ${d.brand||""} ${d.designerName||""}`.toLowerCase().includes(q);
              return statusOk && searchOk;
            })
            .sort((a,b)=>a.status==="pending"?-1:1)
            .map(d=>{
              const count = designOrderCount(d.id);
              const min = d.minOrders || 10;
              const pct = Math.min(100, Math.round((count/min)*100));
              const photoSrc = d.aiRenderUrl || (Array.isArray(d.photos)&&d.photos.length>0 ? (typeof d.photos[0]==="string"?d.photos[0]:d.photos[0]?.url) : null);
              const tagCls = d.status==="approved"||d.status==="funded"||d.status==="in_production"||d.status==="produced" ? "tag-grn" : d.status==="rejected" ? "tag-red" : "tag-gold";
              return (
                <div key={d.id} className="card" style={{marginBottom:14}}>
                  <div style={{display:"flex",gap:14,marginBottom:12}}>
                    {photoSrc && <img src={photoSrc} alt="" style={{width:96,height:96,objectFit:"cover",borderRadius:8,border:"1.5px solid var(--bd)",flexShrink:0}}/>}
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,marginBottom:6}}>
                        <div>
                          <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.1em",marginBottom:3}}>{d.brand||"Custom"} · by {d.designerName} ({d.designerRole}) · {d.createdAt?.slice(0,10)}</div>
                          <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:700}}>{d.name}</div>
                        </div>
                        <div style={{textAlign:"right"}}>
                          <span className={`tag ${tagCls}`}>{d.status.replace("_"," ")}</span>
                          {d.price>0 && <div style={{fontFamily:"'Playfair Display',serif",fontSize:16,color:"var(--gold)",marginTop:6}}>₹{d.price.toLocaleString("en-IN")}</div>}
                        </div>
                      </div>
                      <p style={{fontSize:12.5,color:"var(--ink2)",lineHeight:1.6,margin:"0 0 8px"}}>{d.description}</p>
                      {d.spec && (
                        <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)",lineHeight:1.6}}>
                          {d.spec.caseShape} · {d.spec.caseDiameterMm}mm · {d.spec.dialColour} dial · {d.spec.hands} hands · {d.spec.indices} · {d.spec.strap} strap
                          {d.spec.complications && <> · {d.spec.complications}</>}
                        </div>
                      )}
                      {d.status!=="pending" && d.status!=="rejected" && (
                        <div style={{marginTop:10}}>
                          <div style={{display:"flex",justifyContent:"space-between",fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)",marginBottom:4}}>
                            <span>{count} / {min} pre-orders</span>
                            <span>{pct}%</span>
                          </div>
                          <div style={{height:6,background:"var(--bg3)",borderRadius:3,overflow:"hidden"}}>
                            <div style={{height:"100%",width:`${pct}%`,background:pct>=100?"var(--grn)":"var(--ink)"}}/>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap",borderTop:"1px solid var(--bd)",paddingTop:10}}>
                    {d.status==="pending" && <>
                      <input value={note["d_"+d.id]||""} onChange={e=>setNote(p=>({...p,["d_"+d.id]:e.target.value}))} placeholder="Rejection reason (optional)" style={{flex:1,minWidth:160,fontSize:12}}/>
                      <button className="btn-green" style={{padding:"7px 14px",fontSize:12}} onClick={()=>approveDesign(d)}>Approve ✓</button>
                      <button className="btn-ghost" style={{padding:"7px 14px",fontSize:12,color:"var(--red)",borderColor:"var(--rbd)"}} onClick={()=>rejectDesign(d, note["d_"+d.id])}>Reject</button>
                    </>}
                    {d.status==="approved" && <button className="btn-ghost" style={{padding:"7px 14px",fontSize:12}} onClick={()=>advanceDesign(d,"funded")}>Mark as funded</button>}
                    {d.status==="funded" && <button className="btn-green" style={{padding:"7px 14px",fontSize:12}} onClick={()=>advanceDesign(d,"in_production")}>Start production →</button>}
                    {d.status==="in_production" && <button className="btn-green" style={{padding:"7px 14px",fontSize:12}} onClick={()=>advanceDesign(d,"produced")}>Mark produced ✓</button>}
                    <button className="btn-ghost" style={{padding:"7px 14px",fontSize:12,color:"var(--red)",borderColor:"var(--rbd)",marginLeft:"auto"}} onClick={()=>deleteDesign(d)}>Delete</button>
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {tab==="stories" && <AdminStoriesTab toast={toast}/>}

      {tab==="ai" && <AiDashboard aiEnabled={aiEnabled} toggleAi={toggleAi} maintenance={maintenance} toggleMaintenance={toggleMaintenance} tickerEnabled={tickerEnabled} toggleTicker={toggleTicker} aiBannerEnabled={aiBannerEnabled} toggleAiBanner={toggleAiBanner} navStates={navStates} setNavState={setNavState}/>}

      {tab==="beta" && (
        <div>
          <div style={{background:"var(--bg2)",borderRadius:12,padding:"12px 18px",marginBottom:18,fontSize:13,color:"var(--ink2)",lineHeight:1.7}}>
            Approve requests to send the user a unique beta access code by email. The code is validated against the database on the entry screen.
          </div>
          {(betaRequests||[]).length===0 && <div style={{textAlign:"center",padding:48,fontFamily:"'DM Mono',monospace",fontSize:12,color:"#8A7E74"}}>No beta requests yet.</div>}
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {[...(betaRequests||[])].sort((a,b)=>a.status==="pending"?-1:1).map(r=>(
              <div key={r.id} className="card">
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                  <div>
                    <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.1em",marginBottom:4}}>{r.id} · {(r.created_at||r.createdAt)?.slice(0,10)}</div>
                    <div style={{fontWeight:600,fontSize:16,marginBottom:2}}>{r.name}</div>
                    <div style={{fontSize:13,color:"var(--ink3)"}}>{r.email}</div>
                    {r.beta_code && <div style={{fontFamily:"'DM Mono',monospace",fontSize:11,color:"var(--grn)",marginTop:4}}>Code: {r.beta_code}</div>}
                    {r.admin_note && <div style={{fontSize:12,color:"var(--ink3)",marginTop:4,fontStyle:"italic"}}>{r.admin_note}</div>}
                  </div>
                  <span className={`tag ${r.status==="approved"?"tag-grn":r.status==="rejected"?"tag-red":"tag-gold"}`}>{r.status}</span>
                </div>
                {r.status==="pending" && (
                  <BetaApprovePanel r={r} betaRequests={betaRequests} saveBetaReqs={saveBetaReqs} toast={toast}/>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
// ─── AI Dashboard ─────────────────────────────────────────────────────────────
